import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactAddressOther } from '../queries/contactAddressesQuery';
import { GET_CONTACT_ADDRESSES } from '../queries/contactAddressesQuery';

const UPSERT_CONTACT_PRIMARY_ADDRESS = gql`
  mutation UpsertContactPrimaryAddress(
    $contactId: ID!
    $values: ContactPrimaryAddressUpsertInput!
  ) {
    upsertContactPrimaryAddress(contactId: $contactId, values: $values) {
      ok
      created
      contactPrimaryAddress {
        id
        otherCity
        otherStreet
        otherState
        otherPostalCode
        otherCountryRegion
        otherPhone
        otherPhoneOld
        otherFax
        geocodeRecord
      }
    }
  }
`;

export interface EditOtherAddressDialogProps {
  contactId: string;
  initialValue: ContactAddressOther;
  isOpen: boolean;
  onChange: (data: ContactAddressOther) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactAddressOther;
}

export function EditOtherAddressDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditOtherAddressDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertAddress] = useMutation(UPSERT_CONTACT_PRIMARY_ADDRESS, {
    refetchQueries: [
      { query: GET_CONTACT_ADDRESSES, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertAddress({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactPrimaryAddress?: { ok?: boolean };
      };

      if (data.upsertContactPrimaryAddress?.ok === false) {
        toast.error('Failed to save other address');
      } else {
        toast.success('Other address saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving other address:', error);
      toast.error('Failed to save other address');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Other Address"
    >
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
            Location
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <EditFormField
              label="Street 1"
              onChange={street1 => {
                onChange({ ...value, street1 });
              }}
              value={value.street1}
            />
            <EditFormField
              label="Street 2"
              onChange={street2 => {
                onChange({ ...value, street2 });
              }}
              value={value.street2}
            />
            <EditFormField
              label="City"
              onChange={city => {
                onChange({ ...value, city });
              }}
              value={value.city}
            />
            <EditFormField
              label="State/Province"
              onChange={stateProvince => {
                onChange({ ...value, stateProvince });
              }}
              value={value.stateProvince}
            />
            <EditFormField
              label="ZIP/Postal Code"
              onChange={postalCode => {
                onChange({ ...value, postalCode });
              }}
              value={value.postalCode}
            />
            <EditFormField
              label="Country/Region"
              onChange={countryRegion => {
                onChange({ ...value, countryRegion });
              }}
              value={value.countryRegion}
            />
            <EditFormField
              label="Phone"
              onChange={phone => {
                onChange({ ...value, phone });
              }}
              type="tel"
              value={value.phone}
            />
            <EditFormField
              label="Phone Old"
              onChange={phoneOld => {
                onChange({ ...value, phoneOld });
              }}
              type="tel"
              value={value.phoneOld}
            />
            <EditFormField
              label="Fax"
              onChange={fax => {
                onChange({ ...value, fax });
              }}
              value={value.fax}
            />
            <EditCheckboxField
              checked={value.geocodeRecord}
              label="Geocode Record"
              onCheckedChange={geocodeRecord => {
                onChange({ ...value, geocodeRecord });
              }}
            />
          </div>
        </div>
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactAddressOther,
  initial: ContactAddressOther
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  if (current.street1 !== initial.street1)
    changes.otherStreet = current.street1 || null;
  if (current.city !== initial.city) changes.otherCity = current.city || null;
  if (current.stateProvince !== initial.stateProvince)
    changes.otherState = current.stateProvince || null;
  if (current.postalCode !== initial.postalCode)
    changes.otherPostalCode = current.postalCode || null;
  if (current.countryRegion !== initial.countryRegion)
    changes.otherCountryRegion = current.countryRegion || null;
  if (current.phone !== initial.phone)
    changes.otherPhone = current.phone || null;
  if (current.phoneOld !== initial.phoneOld)
    changes.otherPhoneOld = current.phoneOld || null;
  if (current.fax !== initial.fax) changes.otherFax = current.fax || null;
  if (current.geocodeRecord !== initial.geocodeRecord)
    changes.geocodeRecord = current.geocodeRecord;

  return changes;
}
